import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, toISODate, startOfWeek, addDays, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, CalendarClock, CalendarDays } from "lucide-react";

const WEEKS_PER_MONTH = 4;

const monthRange = () => {
  const now = new Date();
  return [toISODate(new Date(now.getFullYear(), now.getMonth(), 1)),
          toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
};
const weekRange = () => {
  const s = startOfWeek();
  return [toISODate(s), toISODate(addDays(s, 6))];
};

/* ---------------- Manual projection ---------------- */
function ManualForecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/forecast");
      setData(res.data);
      const d = {}; res.data.rows.forEach((r) => (d[r.job_id] = r.projected_weekly_hours));
      setDrafts(d);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const commit = async (jobId, val) => {
    const hours = Math.max(0, parseFloat(val) || 0);
    setDrafts((p) => ({ ...p, [jobId]: hours }));
    try {
      await api.put("/forecast", { job_id: jobId, projected_weekly_hours: hours });
      setData((await api.get("/forecast")).data);
    } catch {}
  };

  const liveRows = (data?.rows || []).map((r) => {
    const raw = drafts[r.job_id];
    const source = raw === undefined || raw === "" ? r.projected_weekly_hours : raw;
    const h = Number.isFinite(parseFloat(source)) ? parseFloat(source) : 0;
    const rate = Number(r.hourly_rate) || 0;
    return { ...r, live_hours: h, live_weekly: h * rate, live_monthly: h * rate * WEEKS_PER_MONTH };
  });
  const totalWeekly = liveRows.reduce((s, r) => s + r.live_weekly, 0);
  const totalMonthly = totalWeekly * WEEKS_PER_MONTH;
  const totalHours = liveRows.reduce((s, r) => s + r.live_hours, 0);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">Type the hours you expect to work per week on each job. Monthly assumes {WEEKS_PER_MONTH} weeks per month.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card className="p-4 sm:p-5"><div className="text-xs text-muted-foreground">Projected weekly hours</div><div className="text-2xl sm:text-3xl font-semibold tabular-nums font-display mt-1" data-testid="forecaster-total-hours">{totalHours.toFixed(1)}</div></Card>
        <Card className="p-4 sm:p-5"><div className="text-xs text-muted-foreground">Estimated weekly income</div><div className="text-2xl sm:text-3xl font-semibold tabular-nums font-display mt-1 text-[color:var(--kpi-positive)]" data-testid="forecaster-total-week">{money(totalWeekly)}</div></Card>
        <Card className="p-4 sm:p-5"><div className="text-xs text-muted-foreground">Estimated monthly income</div><div className="text-2xl sm:text-3xl font-semibold tabular-nums font-display mt-1 text-[color:var(--kpi-positive)]" data-testid="forecaster-total-month">{money(totalMonthly)}</div></Card>
      </div>

      {loading ? <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      : liveRows.length === 0 ? (
        <EmptyState icon={TrendingUp} title="No active jobs to forecast" description="Create active jobs to project your income." />
      ) : (
        <Card className="overflow-hidden">
          <Table data-testid="forecaster-grid">
            <TableHeader><TableRow>
              <TableHead>Job</TableHead><TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-center w-40">Weekly hours</TableHead>
              <TableHead className="text-right">Weekly</TableHead><TableHead className="text-right">Monthly</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {liveRows.map((r) => (
                <TableRow key={r.job_id} data-testid={`forecaster-row-${r.job_id}`} className="hover:bg-accent/50">
                  <TableCell><div className="font-medium">{r.job_title}</div><div className="text-xs text-muted-foreground">{r.client_name}</div></TableCell>
                  <TableCell className="text-right tabular-nums font-mono">{money(r.hourly_rate)}/hr</TableCell>
                  <TableCell className="text-center">
                    <Input type="number" step="0.5" min="0" className="w-24 mx-auto text-center" data-testid={`forecaster-cell-${r.job_id}`}
                      value={drafts[r.job_id] ?? ""}
                      onChange={(e) => setDrafts((p) => ({ ...p, [r.job_id]: e.target.value }))}
                      onBlur={(e) => commit(r.job_id, e.target.value)} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{money(r.live_weekly)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(r.live_monthly)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between p-4 border-t bg-secondary/40">
            <span className="font-medium">Totals</span>
            <div className="flex gap-6">
              <Badge variant="outline" className="tabular-nums">{totalHours.toFixed(1)} hrs/wk</Badge>
              <span className="font-semibold tabular-nums">{money(totalWeekly)}/wk</span>
              <span className="font-semibold tabular-nums text-[color:var(--kpi-positive)]">{money(totalMonthly)}/mo</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---------------- Scheduled projection (reads from Schedule) ---------------- */
function ScheduledForecast() {
  const [window, setWindow] = useState("month");
  const [customFrom, setCustomFrom] = useState(monthRange()[0]);
  const [customTo, setCustomTo] = useState(monthRange()[1]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const range = window === "week" ? weekRange() : window === "month" ? monthRange() : [customFrom, customTo];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [from, to] = range;
      setSummary((await api.get(`/schedule/summary?date_from=${from}&date_to=${to}`)).data);
    } finally { setLoading(false); }
  }, [window, customFrom, customTo]); // eslint-disable-line
  useEffect(() => { loadData(); }, [loadData]);

  const windowLabel = window === "week" ? "this week" : window === "month" ? "this month" : "selected range";
  const weeksInRange = Math.max(1, ((new Date(range[1]) - new Date(range[0])) / 86400000 + 1) / 7);
  const perWeek = summary ? summary.total_income / weeksInRange : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">Income from the blocks you've actually <strong>scheduled</strong> (each job uses its own rate).</p>
        <Button variant="outline" data-testid="forecaster-manage-schedule" onClick={() => navigate("/schedule")}><CalendarDays className="h-4 w-4 mr-2" />Manage schedule</Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Tabs value={window} onValueChange={setWindow}>
          <TabsList data-testid="schedule-window-tabs">
            <TabsTrigger value="week" data-testid="schedule-window-week">This week</TabsTrigger>
            <TabsTrigger value="month" data-testid="schedule-window-month">This month</TabsTrigger>
            <TabsTrigger value="custom" data-testid="schedule-window-custom">Custom</TabsTrigger>
          </TabsList>
        </Tabs>
        {window === "custom" && (
          <div className="flex items-center gap-2">
            <div className="w-40"><DatePicker value={customFrom} onChange={setCustomFrom} testid="schedule-custom-from" /></div>
            <span className="text-muted-foreground text-sm">to</span>
            <div className="w-40"><DatePicker value={customTo} onChange={setCustomTo} testid="schedule-custom-to" /></div>
          </div>
        )}
        <span className="text-xs text-muted-foreground">{fmtDate(range[0])} – {fmtDate(range[1])}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card className="p-4 sm:p-5"><div className="text-xs text-muted-foreground">Scheduled hours ({windowLabel})</div><div className="text-2xl sm:text-3xl font-semibold tabular-nums font-display mt-1" data-testid="schedule-total-hours">{(summary?.total_hours ?? 0).toFixed(1)}</div></Card>
        <Card className="p-4 sm:p-5"><div className="text-xs text-muted-foreground">Expected income ({windowLabel})</div><div className="text-2xl sm:text-3xl font-semibold tabular-nums font-display mt-1 text-[color:var(--kpi-positive)]" data-testid="schedule-total-income">{money(summary?.total_income ?? 0)}</div></Card>
        <Card className="p-4 sm:p-5"><div className="text-xs text-muted-foreground">Avg per week</div><div className="text-2xl sm:text-3xl font-semibold tabular-nums font-display mt-1" data-testid="schedule-per-week">{money(perWeek)}</div></Card>
      </div>

      {loading ? <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      : (!summary || summary.rows.length === 0) ? (
        <EmptyState icon={CalendarClock} title="Nothing scheduled in this window" description="Head to the Schedule page to add specific dates or recurring weekly sessions."
          action={<Button data-testid="schedule-empty-manage" onClick={() => navigate("/schedule")}><CalendarDays className="h-4 w-4 mr-2" />Go to schedule</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="p-4 border-b"><h3 className="text-lg font-semibold font-display">Expected income by job</h3></div>
          <Table data-testid="schedule-summary-table">
            <TableHeader><TableRow>
              <TableHead>Job</TableHead><TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Scheduled hours</TableHead><TableHead className="text-right">Expected income</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {summary.rows.map((r) => (
                <TableRow key={r.job_id} data-testid={`schedule-summary-row-${r.job_id}`} className="hover:bg-accent/50">
                  <TableCell><div className="font-medium">{r.job_title}</div><div className="text-xs text-muted-foreground">{r.client_name}</div></TableCell>
                  <TableCell className="text-right tabular-nums font-mono">{money(r.hourly_rate)}/hr</TableCell>
                  <TableCell className="text-right tabular-nums">{r.hours.toFixed(2)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{money(r.income)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between p-4 border-t bg-secondary/40">
            <span className="font-medium">Totals</span>
            <div className="flex gap-6">
              <Badge variant="outline" className="tabular-nums">{summary.total_hours.toFixed(1)} hrs</Badge>
              <span className="font-semibold tabular-nums text-[color:var(--kpi-positive)]">{money(summary.total_income)}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

export default function Forecaster() {
  const [mode, setMode] = useState("manual");
  return (
    <div>
      <PageHeader title="Income Forecaster" subtitle="Project your income from a manual estimate or from work you've actually scheduled." />
      <Tabs value={mode} onValueChange={setMode} className="mb-6">
        <TabsList data-testid="forecaster-mode-tabs">
          <TabsTrigger value="manual" data-testid="forecaster-mode-manual"><TrendingUp className="h-4 w-4 mr-2" />Manual estimate</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="forecaster-mode-scheduled"><CalendarClock className="h-4 w-4 mr-2" />Scheduled</TabsTrigger>
        </TabsList>
      </Tabs>
      {mode === "manual" ? <ManualForecast /> : <ScheduledForecast />}
    </div>
  );
}
